import { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  FileText, 
  Download, 
  Share2, 
  Trash2, 
  Edit, 
  Users, 
  Lock, 
  Loader2, 
  AlertCircle, 
  CheckCircle,
  Calendar,
  File,
  Tag,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Brain
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { useUserRole } from '../hooks/useUserRole';
import { useScreenSize } from '../hooks/useScreenSize';
import type { 
  UserDocument, 
  DocumentType,
  DocumentAccessLevel,
  DocumentAccessLevelSimple,
  DocumentOwnershipType,
  DocumentShareRequest,
  DocumentSharing,
  DocumentUserAccess,
  PreviouslySharedUser,
  UserDeal
} from '../types/database';
import {
  getDocumentTypeLabel,
  getDocumentOwnershipTypeLabel,
  getDocumentOwnershipTypeColor,
  getDocumentTypesByOwnership,
  formatFileSize
} from '../types/database';

// Configure PDF.js worker - use worker from react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentPreviewPanelProps {
  className?: string;
  selectedDocument: UserDocument | null;
  onDocumentUpdate: () => void;
  onDocumentDelete: (document: UserDocument) => void;
  onExtractionClick?: (document: UserDocument) => void;
}

export function DocumentPreviewPanel({ 
  className = '', 
  selectedDocument,
  onDocumentUpdate,
  onDocumentDelete,
  onExtractionClick
}: DocumentPreviewPanelProps) {
  const { user } = useUser();
  const { isMobile, isTablet } = useScreenSize();
  const { 
    canDownloadDocuments, 
    canShareDocuments, 
    canDeleteDocuments,
    canEditDocuments,
    isLoading: userRoleLoading,
    permissions
  } = useUserRole();

  // Don't render action buttons if user permissions are still loading

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDocumentType, setEditDocumentType] = useState<DocumentType>('other');
  const [editAccessLevel, setEditAccessLevel] = useState<DocumentAccessLevel>('private');
  const [editAccessLevelSimple, setEditAccessLevelSimple] = useState<DocumentAccessLevelSimple>('private');
  const [editOwnershipType, setEditOwnershipType] = useState<DocumentOwnershipType>('buyer');
  const [userDeals, setUserDeals] = useState<UserDeal[]>([]);
  const [selectedDeals, setSelectedDeals] = useState<string[]>([]);
  const [associationType, setAssociationType] = useState<'specific' | 'all_deals'>('specific');
  
  // User sharing functionality state
  const [showUserShareModal, setShowUserShareModal] = useState(false);
  const [documentUsers, setDocumentUsers] = useState<DocumentUserAccess[]>([]);
  const [previouslySharedUsers, setPreviouslySharedUsers] = useState<PreviouslySharedUser[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [selectedSharedUser, setSelectedSharedUser] = useState<string>('');
  const [isSharing, setIsSharing] = useState(false);
  
  // PDF viewer state
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.8);
  const [pdfError, setPdfError] = useState<string | null>(null);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareAccessType, setShareAccessType] = useState<'read' | 'download'>('read');
  const [shareExpiresHours, setShareExpiresHours] = useState(24);
  const [shareMaxDownloads, setShareMaxDownloads] = useState<number | undefined>();
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [documentShares, setDocumentShares] = useState<DocumentSharing[]>([]);

  // Load user deals
  useEffect(() => {
    const loadUserDeals = async () => {
      if (!user?.id) return;
      
      try {
        const response = await DatabaseService.getUserDeals('read', user.id);
        if (response.data) {
          setUserDeals(response.data);
        }
      } catch (err) {
        console.error('Failed to load user deals:', err);
      }
    };

    loadUserDeals();
  }, [user?.id]);

  // Load document preview when document is selected
  useEffect(() => {
    if (selectedDocument) {
      loadDocumentPreview();
      loadDocumentShares();
      // [DEBUG - DELETE WHEN FINISHED] Document sharing is now at the deal level, not document level
      // loadDocumentUsers(); // DELETED: function uses has_document_download_access which no longer exists
      // loadPreviouslySharedUsers(); // DELETED: function get_previously_shared_users no longer exists
      setEditTitle(selectedDocument.title);
      setEditDescription(selectedDocument.description || '');
      setEditDocumentType(selectedDocument.document_type);
      setEditAccessLevel(selectedDocument.access_level);
      setEditAccessLevelSimple(selectedDocument.access_level_simple || (selectedDocument.access_level === 'shared' ? 'shared' : 'private'));
      setEditOwnershipType(selectedDocument.ownership_type || 'buyer');
      
      // Load existing deal associations
      if (selectedDocument.associated_deals) {
        setSelectedDeals(selectedDocument.associated_deals.map(deal => deal.deal_id));
        // Set association type based on existing associations
        const hasAllDeals = selectedDocument.associated_deals.some(deal => deal.association_type === 'all_deals');
        setAssociationType(hasAllDeals ? 'all_deals' : 'specific');
      } else {
        setSelectedDeals([]);
        setAssociationType('specific');
      }
      
      // Reset PDF viewer state
      setNumPages(null);
      setPageNumber(1);
      setScale(1.0);
      setPdfError(null);
    } else {
      setPreviewUrl(null);
      setDocumentShares([]);
    }
  }, [selectedDocument]);

  // PDF viewer event handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = () => {
    setPdfError('Failed to load PDF. The file may be corrupted or not a valid PDF.');
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  // Load document preview
  const loadDocumentPreview = async () => {
    if (!selectedDocument) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await DatabaseService.downloadDocument(selectedDocument.id, user?.id);
      
      if (response.error) {
        setError(response.error);
        console.error('Document preview error:', response.error);
      } else if (response.data) {
        setPreviewUrl(response.data.url);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load document preview';
      console.error('Document preview exception:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Load document shares
  const loadDocumentShares = async () => {
    if (!selectedDocument) return;

    try {
      const response = await DatabaseService.getDocumentShares(selectedDocument.id);
      if (response.data) {
        setDocumentShares(response.data);
      }
    } catch (err) {
      console.error('Failed to load document shares:', err);
    }
  };

  // [DEBUG - DELETE WHEN FINISHED] Document sharing is now handled at the deal level
  // Load document users with access - DEPRECATED
  // const loadDocumentUsers = async () => {
  //   if (!selectedDocument || !user) return;
  //   
  //   try {
  //     const response = await DatabaseService.getDocumentUsersWithAccess(selectedDocument.id, user.id);
  //     if (response.data) {
  //       setDocumentUsers(response.data);
  //     } else if (response.error) {
  //       console.error('Error loading document users:', response.error);
  //     }
  //   } catch (error) {
  //     console.error('Error loading document users:', error);
  //   }
  // };

  // Load previously shared users - DEPRECATED
  // const loadPreviouslySharedUsers = async () => {
  //   if (!user) return;
  //   
  //   try {
  //     const response = await DatabaseService.getPreviouslySharedUsers(user.id);
  //     if (response.data) {
  //       setPreviouslySharedUsers(response.data);
  //     } else if (response.error) {
  //       console.error('Error loading previously shared users:', response.error);
  //     }
  //   } catch (error) {
  //     console.error('Error loading previously shared users:', error);
  //   }
  // };

  // Handle sharing document
  const handleShareDocument = async () => {
    if (!selectedDocument || !user) return;
    
    setIsSharing(true);
    try {
      let emailToShare = '';
      
      if (selectedSharedUser) {
        // Share with previously shared user
        const user = previouslySharedUsers.find(u => u.user_id === selectedSharedUser);
        if (user) {
          emailToShare = user.email;
        }
      } else if (shareEmail) {
        // Share with new email
        emailToShare = shareEmail;
      }
      
      if (!emailToShare) {
        setError('Please enter an email address or select a user');
        return;
      }
      
      const response = await DatabaseService.grantDocumentAccessByEmail(
        selectedDocument.id,
        emailToShare,
        'read',
        undefined,
        user.id
      );
      
      if (response.data?.success) {
        setMessage(response.data.message);
        
        // If it's an invitation (user_id is null), send email
        if (!response.data.user_id) {
          try {
            const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
            const formData = new FormData();
            formData.append('email', emailToShare);
            formData.append('document_title', selectedDocument.title);
            formData.append('inviter_name', user.fullName || user.firstName || 'User');
            formData.append('inviter_email', user.primaryEmailAddress?.emailAddress || '');
            
            const emailResponse = await fetch(`${backendUrl}/send-invitation`, {
              method: 'POST',
              body: formData
            });
            
            if (emailResponse.ok) {
              setMessage('Invitation sent successfully! The user will receive an email to join the platform.');
            } else {
              console.error('Failed to send invitation email');
              setMessage('Document access granted, but failed to send invitation email. Please contact the user directly.');
            }
          } catch (emailError) {
            console.error('Error sending invitation email:', emailError);
            setMessage('Document access granted, but failed to send invitation email. Please contact the user directly.');
          }
        }
        
        setShareEmail('');
        setSelectedSharedUser('');
        setShowUserShareModal(false);
        // Reload document users and update access level
        await loadDocumentUsers();
        await loadDocumentPreview();
      } else {
        setError(response.error || response.data?.message || 'Failed to share document');
      }
    } catch (error) {
      console.error('Error sharing document:', error);
      setError('Failed to share document');
    } finally {
      setIsSharing(false);
    }
  };

  // Handle revoking access
  const handleRevokeAccess = async (targetUserId: string) => {
    if (!selectedDocument || !user) return;
    
    try {
      const response = await DatabaseService.revokeDocumentAccess(
        selectedDocument.id,
        targetUserId,
        user.id
      );
      
      if (response.data?.success) {
        setMessage(response.data.message);
        // Reload document users and update access level
        await loadDocumentUsers();
        await loadDocumentPreview();
      } else {
        setError(response.error || response.data?.message || 'Failed to revoke access');
      }
    } catch (error) {
      console.error('Error revoking access:', error);
      setError('Failed to revoke access');
    }
  };

  // Handle access level toggle
  const handleAccessLevelToggle = async (newAccessLevel: DocumentAccessLevelSimple) => {
    if (!selectedDocument || !user) return;
    
    try {
      const response = await DatabaseService.updateDocumentAccessLevel(
        selectedDocument.id,
        newAccessLevel,
        user.id
      );
      
      if (response.data) {
        setEditAccessLevelSimple(newAccessLevel);
        setMessage('Access level updated successfully');
        await loadDocumentPreview();
      } else {
        setError(response.error || 'Failed to update access level');
      }
    } catch (error) {
      console.error('Error updating access level:', error);
      setError('Failed to update access level');
    }
  };

  // Handle document download
  const handleDownload = async () => {
    if (!selectedDocument) return;

    try {
      const response = await DatabaseService.downloadDocument(selectedDocument.id, user?.id);
      
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setMessage('Document download started');
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download document';
      setError(errorMessage);
    }
  };

  // Handle document deletion
  const handleDelete = async () => {
    if (!selectedDocument) return;

    try {
      const response = await DatabaseService.deleteDocument(selectedDocument.id, user?.id);
      
      if (response.error) {
        setError(response.error);
      } else {
        setMessage('Document deleted successfully');
        onDocumentDelete(selectedDocument);
        onDocumentUpdate();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete document';
      setError(errorMessage);
    }
  };

  // Handle document update
  const handleUpdate = async () => {
    if (!selectedDocument) return;
    
    // Check if user is authenticated
    if (!user?.id) {
      setError('User not authenticated. Please refresh the page and try again.');
      return;
    }

    setIsLoading(true);
    try {
      
      // Update basic document properties
      const response = await DatabaseService.updateDocument(selectedDocument.id, {
        title: editTitle,
        description: editDescription,
        document_type: editDocumentType,
        access_level: editAccessLevel,
        ownership_type: editOwnershipType
      }, user.id);
      
      if (response.error) {
        setError(response.error);
        return;
      }

      // Update deal associations
      
      if (editOwnershipType === 'buyer' && userDeals.length > 0) {
        // Remove existing associations first
        if (selectedDocument.associated_deals) {
          for (const existingDeal of selectedDocument.associated_deals) {
            await DatabaseService.removeDocumentDealAssociation(
              selectedDocument.id, 
              existingDeal.deal_id, 
              user?.id
            );
          }
        }

        // Add new associations
        if (associationType === 'all_deals') {
          // Associate with all deals
          
          // Double-check user authentication before proceeding
          if (!user?.id) {
            setError('User authentication lost during operation. Please refresh and try again.');
            return;
          }
          
          for (const deal of userDeals) {
            console.log('Associating with deal:', deal.title, deal.deal_id);
            const result = await DatabaseService.associateDocumentWithDeal({
              document_id: selectedDocument.id,
              deal_id: deal.deal_id,
              association_type: 'all_deals'
            }, user.id); // Use user.id instead of user?.id since we've already checked
            console.log('Association result:', result);
            if (result.error) {
              console.error('Association failed:', result.error);
              setError(`Failed to associate with deal "${deal.title}": ${result.error}`);
              return;
            }
          }
        } else if (selectedDeals.length > 0) {
          // Associate with specific deals
          for (const dealId of selectedDeals) {
            const result = await DatabaseService.associateDocumentWithDeal({
              document_id: selectedDocument.id,
              deal_id: dealId,
              association_type: 'specific'
            }, user.id);
            if (result.error) {
              console.error('Association failed:', result.error);
              setError(`Failed to associate with deal: ${result.error}`);
              return;
            }
          }
        }
      } else if (editOwnershipType === 'seller' && selectedDeals.length > 0) {
        // For seller documents, only allow one deal association
        const dealId = selectedDeals[0];
        
        // Remove existing associations first
        if (selectedDocument.associated_deals) {
          for (const existingDeal of selectedDocument.associated_deals) {
            await DatabaseService.removeDocumentDealAssociation(
              selectedDocument.id, 
              existingDeal.deal_id, 
              user?.id
            );
          }
        }

        // Add single deal association
        const result = await DatabaseService.associateDocumentWithDeal({
          document_id: selectedDocument.id,
          deal_id: dealId,
          association_type: 'specific'
        }, user.id);
        if (result.error) {
          console.error('Association failed:', result.error);
          setError(`Failed to associate with deal: ${result.error}`);
          return;
        }
      }

      setMessage('Document updated successfully');
      setIsEditing(false);
      onDocumentUpdate();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update document';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle document sharing
  const handleCreateShare = async () => {
    if (!selectedDocument) return;

    setIsCreatingShare(true);
    try {
      const shareRequest: DocumentShareRequest = {
        document_id: selectedDocument.id,
        access_type: shareAccessType,
        expires_hours: shareExpiresHours,
        max_downloads: shareMaxDownloads || undefined
      };

      const response = await DatabaseService.createDocumentShare(shareRequest);

      if (response.error) {
        setError(response.error);
      } else {
        setMessage('Share link created successfully');
        setShowShareModal(false);
        await loadDocumentShares();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create share link';
      setError(errorMessage);
    } finally {
      setIsCreatingShare(false);
    }
  };

  // Handle share revocation
  const handleRevokeShare = async (shareId: string) => {
    try {
      const response = await DatabaseService.revokeDocumentShare(shareId);
      
      if (response.error) {
        setError(response.error);
      } else {
        setMessage('Share link revoked successfully');
        await loadDocumentShares();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke share link';
      setError(errorMessage);
    }
  };

  // Copy share link to clipboard
  const copyShareLink = (shareUrl: string) => {
    navigator.clipboard.writeText(shareUrl);
    setMessage('Share link copied to clipboard');
    setTimeout(() => setMessage(null), 3000);
  };

  // Clear messages after timeout
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!selectedDocument) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-50 ${className}`}>
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Document Selected</h3>
          <p className="text-gray-600">
            Select a document from the list to view its details and preview.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Header */}
      <div className={`${isMobile ? 'p-4' : isTablet ? 'p-5' : 'p-6'} border-b border-gray-200`}>
        <div className={`flex items-start justify-between ${isMobile ? 'mb-3' : isTablet ? 'mb-4' : 'mb-4'}`}>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2 text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Document title"
                  />
                </div>
                <div>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Document description"
                  />
                </div>
                {/* Ownership Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Document Type</label>
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEditOwnershipType('buyer');
                        // Reset document type to first available type for buyer
                        const buyerTypes = getDocumentTypesByOwnership('buyer');
                        setEditDocumentType(buyerTypes[0]);
                      }}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        editOwnershipType === 'buyer'
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Buyer Document
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditOwnershipType('seller');
                        // Reset document type to first available type for seller
                        const sellerTypes = getDocumentTypesByOwnership('seller');
                        setEditDocumentType(sellerTypes[0]);
                      }}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        editOwnershipType === 'seller'
                          ? 'bg-green-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Seller Document
                    </button>
                  </div>
                </div>

                <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                    <select
                      value={editDocumentType}
                      onChange={(e) => setEditDocumentType(e.target.value as DocumentType)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {getDocumentTypesByOwnership(editOwnershipType).map((type) => (
                        <option key={type} value={type}>
                          {getDocumentTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleAccessLevelToggle('private')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          editAccessLevelSimple === 'private'
                            ? 'bg-red-100 text-red-800 border-2 border-red-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                        }`}
                      >
                        <Lock className="w-4 h-4" />
                        Private
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAccessLevelToggle('shared')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          editAccessLevelSimple === 'shared'
                            ? 'bg-green-100 text-green-800 border-2 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        Shared
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Shared Users Section */}
                {editAccessLevelSimple === 'shared' && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Shared Users</h4>
                      <button
                        type="button"
                        onClick={() => setShowUserShareModal(true)}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        <Share2 className="w-4 h-4" />
                        Share
                      </button>
                    </div>
                    
                    {documentUsers.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-2">No users have access to this document</p>
                        <p className="text-xs text-gray-400">Click "Share" to invite users.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {documentUsers.map((user) => (
                          <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {user.first_name && user.last_name 
                                    ? `${user.first_name} ${user.last_name}` 
                                    : user.email}
                                </span>
                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                  {user.role}
                                </span>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  user.status === 'owner' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {user.status === 'owner' ? 'Owner' : 'Shared'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                            </div>
                            {user.status !== 'owner' && (
                              <button
                                type="button"
                                onClick={() => handleRevokeAccess(user.user_id)}
                                className="p-1 text-red-500 hover:text-red-700 transition-colors"
                                title="Revoke Access"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Deal Association Section */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Deal Associations</h4>
                  
                  {userDeals.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 mb-2">No deals available</p>
                      <p className="text-xs text-gray-400">Create a deal first to associate documents with it.</p>
                    </div>
                  ) : (
                    <>
                      {editOwnershipType === 'buyer' && (
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Association Type</label>
                          <div className="flex gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="specific"
                                checked={associationType === 'specific'}
                                onChange={(e) => setAssociationType(e.target.value as 'specific' | 'all_deals')}
                                className="mr-2"
                              />
                              <span className="text-sm">Specific Deals</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="all_deals"
                                checked={associationType === 'all_deals'}
                                onChange={(e) => setAssociationType(e.target.value as 'specific' | 'all_deals')}
                                className="mr-2"
                              />
                              <span className="text-sm">All Deals</span>
                            </label>
                          </div>
                        </div>
                      )}
                      
                      {associationType === 'specific' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {editOwnershipType === 'seller' ? 'Associated Deal' : 'Associated Deals'}
                          </label>
                          
                          {/* Selected Deals as Tags */}
                          {selectedDeals.length > 0 && (
                            <div className="mb-3">
                              <div className="flex flex-wrap gap-2">
                                {selectedDeals.map((dealId) => {
                                  const deal = userDeals.find(d => d.deal_id === dealId);
                                  return (
                                    <span
                                      key={dealId}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full border border-blue-200"
                                    >
                                      {deal?.title || `Deal ${dealId.slice(0, 8)}`}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedDeals(selectedDeals.filter(id => id !== dealId));
                                        }}
                                        className="ml-1 text-blue-600 hover:text-blue-800"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Deal Selection */}
                          <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                            {userDeals.map((deal) => (
                              <label key={deal.deal_id} className="flex items-center mb-2 hover:bg-gray-50 p-1 rounded">
                                <input
                                  type={editOwnershipType === 'seller' ? 'radio' : 'checkbox'}
                                  name={editOwnershipType === 'seller' ? 'deal' : 'deals'}
                                  value={deal.deal_id}
                                  checked={selectedDeals.includes(deal.deal_id)}
                                  onChange={(e) => {
                                    if (editOwnershipType === 'seller') {
                                      setSelectedDeals([deal.deal_id]);
                                    } else {
                                      if (e.target.checked) {
                                        setSelectedDeals([...selectedDeals, deal.deal_id]);
                                      } else {
                                        setSelectedDeals(selectedDeals.filter(id => id !== deal.deal_id));
                                      }
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-sm text-gray-700">{deal.title}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {editOwnershipType === 'seller' && (
                        <p className="text-xs text-gray-500 mt-2">
                          Seller documents can only be associated with one deal.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{selectedDocument.title}</h2>
                {selectedDocument.description && (
                  <p className="text-gray-600 mb-3">{selectedDocument.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    {getDocumentTypeLabel(selectedDocument.document_type)}
                  </span>
                  <span className="flex items-center gap-1">
                    <File className="w-4 h-4" />
                    {formatFileSize(selectedDocument.file_size)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedDocument.uploaded_at).toLocaleDateString()}
                  </span>
                  {selectedDocument.ownership_type && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDocumentOwnershipTypeColor(selectedDocument.ownership_type)}`}>
                      {getDocumentOwnershipTypeLabel(selectedDocument.ownership_type)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className={`flex items-center gap-2 ${isMobile ? 'ml-2' : isTablet ? 'ml-3' : 'ml-4'}`}>
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className={`${isMobile ? 'p-3' : isTablet ? 'p-2' : 'p-2'} text-gray-500 hover:text-gray-700 transition-colors`}
                  title="Cancel"
                >
                  <X className={`${isMobile ? 'w-5 h-5' : isTablet ? 'w-4 h-4' : 'w-4 h-4'}`} />
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={isLoading || !editTitle.trim()}
                  className={`${isMobile ? 'p-3' : isTablet ? 'p-2' : 'p-2'} text-green-600 hover:text-green-800 transition-colors disabled:opacity-50`}
                  title="Save Changes"
                >
                  <CheckCircle className={`${isMobile ? 'w-5 h-5' : isTablet ? 'w-4 h-4' : 'w-4 h-4'}`} />
                </button>
              </>
            ) : (
              <>
                {/* Always show extraction button regardless of loading state */}
                <button
                  onClick={() => {
                    if (onExtractionClick) {
                      onExtractionClick(selectedDocument);
                    } else {
                      console.error('onExtractionClick is not defined!');
                    }
                  }}
                  className={`${isMobile ? 'p-3' : 'p-2'} text-purple-600 hover:text-purple-800 transition-colors border-2 border-purple-500 rounded-lg bg-purple-50`}
                  title="Extract Data"
                >
                  <Brain className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                </button>

                {/* Show loading indicator if permissions are still loading */}
                {userRoleLoading && (
                  <div className={`${isMobile ? 'p-3' : 'p-2'} text-gray-400`}>
                    <Loader2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} animate-spin`} />
                    <span className="text-xs">Loading permissions...</span>
                  </div>
                )}

                {/* Other action buttons */}
                {!userRoleLoading && (
                  <>
                    {canEditDocuments && selectedDocument.can_admin && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className={`${isMobile ? 'p-3' : 'p-2'} text-blue-600 hover:text-blue-800 transition-colors`}
                        title="Edit Document"
                      >
                        <Edit className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                      </button>
                    )}

                {canShareDocuments && selectedDocument.can_admin && (
                  <button
                    onClick={() => setShowUserShareModal(true)}
                    className={`${isMobile ? 'p-3' : 'p-2'} text-purple-600 hover:text-purple-800 transition-colors`}
                    title="Share Document"
                  >
                    <Share2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  </button>
                )}
                
                {canDownloadDocuments && selectedDocument.can_read && (
                  <button
                    onClick={handleDownload}
                    className={`${isMobile ? 'p-3' : 'p-2'} text-blue-600 hover:text-blue-800 transition-colors`}
                    title="Download"
                  >
                    <Download className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  </button>
                )}
                
                {canShareDocuments && selectedDocument.can_admin && (
                  <button
                    onClick={() => {
                      setShowShareModal(true);
                      loadDocumentShares();
                    }}
                    className={`${isMobile ? 'p-3' : 'p-2'} text-green-600 hover:text-green-800 transition-colors`}
                    title="Share"
                  >
                    <Share2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  </button>
                )}
                
                    {canDeleteDocuments && selectedDocument.can_admin && (
                      <button
                        onClick={handleDelete}
                        className={`${isMobile ? 'p-3' : 'p-2'} text-red-600 hover:text-red-800 transition-colors`}
                        title="Delete"
                      >
                        <Trash2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Deal Associations and Access Level */}
        <div className="flex items-center gap-3">
          {/* Deal Associations */}
          <div className="flex items-center gap-2">
            {selectedDocument.associated_deals && selectedDocument.associated_deals.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {selectedDocument.associated_deals.map((deal) => (
                  <span
                    key={deal.deal_id}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                  >
                    {deal.deal_title || `Deal ${deal.deal_id.slice(0, 8)}`}
                  </span>
                ))}
              </div>
            ) : (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                No deals assigned
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {(() => {
              const accessLevel = selectedDocument.access_level_simple || (selectedDocument.access_level === 'shared' ? 'shared' : 'private');
              return (
                <>
                  {accessLevel === 'private' && <Lock className="w-4 h-4 text-red-500" />}
                  {accessLevel === 'shared' && <Users className="w-4 h-4 text-green-500" />}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    accessLevel === 'private' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {accessLevel === 'private' ? 'Private' : 'Shared'}
                  </span>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-800">{message}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Document Details */}
      <div className={`${isMobile ? 'p-4' : isTablet ? 'p-5' : 'p-6'} border-b border-gray-200`}>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Document Details</h3>
        <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-1' : 'grid-cols-2'} gap-4 text-sm`}>
          <div>
            <span className="text-gray-500">File Name:</span>
            <p className="font-medium text-gray-900">{selectedDocument.file_name}</p>
          </div>
          <div>
            <span className="text-gray-500">MIME Type:</span>
            <p className="font-medium text-gray-900">{selectedDocument.mime_type}</p>
          </div>
          <div>
            <span className="text-gray-500">Version:</span>
            <p className="font-medium text-gray-900">{selectedDocument.version}</p>
          </div>
          <div>
            <span className="text-gray-500">Uploaded By:</span>
            <p className="font-medium text-gray-900">{selectedDocument.uploaded_by || selectedDocument.owner_id || 'Unknown User'}</p>
          </div>
          {selectedDocument.ownership_type && (
            <div>
              <span className="text-gray-500">Ownership Type:</span>
              <p className="font-medium text-gray-900">{getDocumentOwnershipTypeLabel(selectedDocument.ownership_type)}</p>
            </div>
          )}
        </div>
        
        {/* Associated Deals */}
        {selectedDocument.associated_deals && selectedDocument.associated_deals.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Associated Deals</h4>
            <div className="space-y-1">
              {selectedDocument.associated_deals.map((deal) => (
                <div key={deal.deal_id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{deal.deal_title || deal.deal_id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    deal.association_type === 'all_deals' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {deal.association_type === 'all_deals' ? 'All Deals' : 'Specific Deal'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Document Preview */}
      <div className={`flex-1 ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-4'}`}>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Preview</h3>
        <div className={`border border-gray-200 rounded-lg ${isMobile ? 'h-[calc(100vh-300px)]' : isTablet ? 'h-[calc(100vh-250px)]' : 'h-[calc(100vh-200px)]'} overflow-hidden relative`}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading preview...</span>
            </div>
          ) : previewUrl ? (
            <div className="w-full h-full flex flex-col">
              {/* PDF Preview using react-pdf */}
              {selectedDocument.mime_type === 'application/pdf' ? (
                <div className="flex-1 flex flex-col">
                  {/* PDF Controls */}
                  <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gray-50 border-b border-gray-200 ${isMobile ? 'flex flex-col gap-3' : 'flex items-center justify-between'}`}>
                    {isMobile ? (
                      // Mobile PDF Controls - Stacked
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={goToPrevPage}
                              disabled={pageNumber <= 1}
                              className={`${isMobile ? 'p-2' : 'p-1'} text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                              title="Previous Page"
                            >
                              <ChevronLeft className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                            </button>
                            <span className="text-sm text-gray-600">
                              Page {pageNumber} of {numPages || '...'}
                            </span>
                            <button
                              onClick={goToNextPage}
                              disabled={pageNumber >= (numPages || 1)}
                              className={`${isMobile ? 'p-2' : 'p-1'} text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                              title="Next Page"
                            >
                              <ChevronRight className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                            </button>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={zoomOut}
                              className={`${isMobile ? 'p-2' : 'p-1'} text-gray-600 hover:text-gray-800`}
                              title="Zoom Out"
                            >
                              <ZoomOut className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                            </button>
                            <span className="text-sm text-gray-600 min-w-[3rem] text-center">
                              {Math.round(scale * 100)}%
                            </span>
                            <button
                              onClick={zoomIn}
                              className={`${isMobile ? 'p-2' : 'p-1'} text-gray-600 hover:text-gray-800`}
                              title="Zoom In"
                            >
                              <ZoomIn className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors text-center"
                          >
                            Open in New Tab
                          </a>
                          <button
                            onClick={handleDownload}
                            className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                          >
                            Download
                          </button>
                        </div>
                      </>
                    ) : (
                      // Desktop PDF Controls - Horizontal
                      <>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={goToPrevPage}
                            disabled={pageNumber <= 1}
                            className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous Page"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="text-sm text-gray-600">
                            Page {pageNumber} of {numPages || '...'}
                          </span>
                          <button
                            onClick={goToNextPage}
                            disabled={pageNumber >= (numPages || 1)}
                            className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next Page"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={zoomOut}
                            className="p-1 text-gray-600 hover:text-gray-800"
                            title="Zoom Out"
                          >
                            <ZoomOut className="w-4 h-4" />
                          </button>
                          <span className="text-sm text-gray-600 min-w-[3rem] text-center">
                            {Math.round(scale * 100)}%
                          </span>
                          <button
                            onClick={zoomIn}
                            className="p-1 text-gray-600 hover:text-gray-800"
                            title="Zoom In"
                          >
                            <ZoomIn className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex gap-2">
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          >
                            Open in New Tab
                          </a>
                          <button
                            onClick={handleDownload}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                          >
                            Download
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* PDF Viewer */}
                  <div className={`flex-1 overflow-auto bg-gray-100 ${isMobile ? 'p-2' : 'p-4'}`}>
                    {pdfError ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <AlertCircle className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} text-red-500 mx-auto mb-2`} />
                          <p className={`${isMobile ? 'text-sm' : 'text-base'} text-red-600 mb-2`}>{pdfError}</p>
                          <button
                            onClick={handleDownload}
                            className={`${isMobile ? 'px-3 py-2 text-sm' : 'px-4 py-2'} bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors`}
                          >
                            Download Document
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`w-full h-full overflow-auto ${isMobile ? 'p-2' : 'p-4'}`}>
                        <div className="flex justify-center">
                          <Document
                            file={previewUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={
                              <div className="flex items-center justify-center h-96">
                                <Loader2 className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} animate-spin text-blue-600`} />
                                <span className={`ml-2 ${isMobile ? 'text-sm' : 'text-base'} text-gray-600`}>Loading PDF...</span>
                              </div>
                            }
                          >
                            <Page
                              pageNumber={pageNumber}
                              width={isMobile ? Math.min(400 * scale, window.innerWidth - 40) : 800 * scale}
                              renderTextLayer={true}
                              renderAnnotationLayer={true}
                            />
                          </Document>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  {/* File type specific preview */}
                  {selectedDocument.mime_type?.includes('spreadsheet') || 
                   selectedDocument.mime_type?.includes('excel') ||
                   selectedDocument.file_name?.endsWith('.xlsx') ||
                   selectedDocument.file_name?.endsWith('.xls') ? (
                    <div className="flex-1 flex flex-col">
                      <div className="p-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            Spreadsheet Preview
                          </span>
                          <div className="flex gap-2">
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              Open in New Tab
                            </a>
                            <button
                              onClick={handleDownload}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex items-center justify-center bg-gray-100">
                        <div className="text-center">
                          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 mb-2">Spreadsheet preview not available</p>
                          <p className="text-sm text-gray-500 mb-4">
                            Use "Open in New Tab" to view in Google Sheets or download to open in Excel
                          </p>
                          <a
                            href={`https://docs.google.com/spreadsheets/d/1/edit?usp=sharing&url=${encodeURIComponent(previewUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            Open in Google Sheets
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1">
                      <iframe
                        src={previewUrl}
                        className="w-full h-full border-0"
                        title={`Preview of ${selectedDocument.title}`}
                        onError={() => {
                          setError('Preview failed to load. Click download to view the document.');
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto mb-2" />
                <p>Preview not available</p>
                <p className="text-sm">This file type cannot be previewed</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-white rounded-xl shadow-xl ${isMobile ? 'max-w-sm' : 'max-w-2xl'} w-full ${isMobile ? 'mx-2' : 'mx-4'}`}>
            <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Share "{selectedDocument.title}"
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Type
                  </label>
                  <select
                    value={shareAccessType}
                    onChange={(e) => setShareAccessType(e.target.value as 'read' | 'download')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="read">View Only</option>
                    <option value="download">Download</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expires In (Hours)
                  </label>
                  <input
                    type="number"
                    value={shareExpiresHours}
                    onChange={(e) => setShareExpiresHours(parseInt(e.target.value) || 24)}
                    min="1"
                    max="168"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Downloads (Optional)
                  </label>
                  <input
                    type="number"
                    value={shareMaxDownloads || ''}
                    onChange={(e) => setShareMaxDownloads(e.target.value ? parseInt(e.target.value) : undefined)}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              {/* Existing Shares */}
              {documentShares.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Active Share Links</h4>
                  <div className="space-y-2">
                    {documentShares.map((share) => (
                      <div key={share.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {share.access_type === 'read' ? 'View Only' : 'Download'}
                          </div>
                          <div className="text-xs text-gray-600">
                            Expires: {new Date(share.expires_at).toLocaleString()}
                            {share.max_downloads && ` • Max downloads: ${share.max_downloads}`}
                            {share.download_count > 0 && ` • Used: ${share.download_count}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => copyShareLink(`${window.location.origin}/shared/${share.share_token}`)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Copy
                          </button>
                          <button
                            onClick={() => handleRevokeShare(share.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-3'} mt-6`}>
                <button
                  onClick={() => setShowShareModal(false)}
                  className={`flex-1 ${isMobile ? 'px-4 py-3' : 'px-4 py-2'} border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors`}
                >
                  Close
                </button>
                <button
                  onClick={handleCreateShare}
                  disabled={isCreatingShare}
                  className={`flex-1 flex items-center justify-center gap-2 ${isMobile ? 'px-4 py-3' : 'px-4 py-2'} bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50`}
                >
                  {isCreatingShare ? (
                    <Loader2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} animate-spin`} />
                  ) : (
                    <Share2 className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
                  )}
                  {isCreatingShare ? 'Creating...' : 'Create Share Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Share Modal */}
      {showUserShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Share Document</h3>
                <button
                  onClick={() => setShowUserShareModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Previously Shared Users */}
                {previouslySharedUsers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Previously Shared Users
                    </label>
                    <select
                      value={selectedSharedUser}
                      onChange={(e) => {
                        setSelectedSharedUser(e.target.value);
                        setShareEmail('');
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a user...</option>
                      {previouslySharedUsers.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name} (${user.email})` 
                            : user.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Divider */}
                {previouslySharedUsers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <span className="text-sm text-gray-500">or</span>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => {
                      setShareEmail(e.target.value);
                      setSelectedSharedUser('');
                    }}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    User must be registered on the platform to receive access.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowUserShareModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleShareDocument}
                    disabled={isSharing || (!shareEmail && !selectedSharedUser)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {isSharing ? 'Sharing...' : 'Share Document'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
